import { EDITOR_DECIMAL_BASE } from '@editor/config/constants'
import {
  INSPECTION_CONTRACTEE_ERROR_COLOR,
  INSPECTION_CONTRACTOR_ERROR_COLOR,
  INSPECTION_NORMAL_COLOR,
} from '@editor/config/styles'
import {
  InspectionItem,
  InspectionItemNumberValues,
  InspectionItemPreDefinedThresholdsKey,
  InspectionItemPredefinedThresholds,
  InspectionItemType,
  InspectionItemUserDefinedThresholds,
  InspectionSheet,
  PlaneInspectionItems,
  ProjectSheetSettings,
} from '@editor/interfaces/interfaces'
import axios, { CanceledError } from 'axios'
import { debounce } from 'lodash'

import { ERROR_PROCESS, processErrorHandler } from './ErrorHandler'
import { GET_INSPECTION_AREA_API_URL, parseInspectionItemPartitionKey } from './InspectionArea'
import { GET_RPOJECTS_API_URL } from './Projects'
import { meterToMillimeter, millimeterToMeter, roundNumber, zeroPad } from './Util'

export const GET_INSPECTION_SHEET_API_URL = (projectId: string, inspectionAreaId: string, inspectionSheetId?: string) =>
  [GET_INSPECTION_AREA_API_URL(projectId, inspectionAreaId), 'inspection-sheets', inspectionSheetId]
    .filter((v) => v !== undefined)
    .join('/')

/**
 * Check if the item is an inspection item.
 *
 * @param item Item to check
 * @returns
 */
export const isInspectionItem = (item?: object): item is InspectionItem => !!item && 'inspection_item_id' in item

/**
 * Get inspection sheets for an inspection area.
 * @param access_token
 * @param projectId
 * @param inspectionAreaId
 * @param showErrorModal
 * @returns
 */
export const getInspectionSheets = async (
  access_token: string,
  projectId: string,
  inspectionAreaId: string,
  showErrorModal?: (message: string) => void,
): Promise<InspectionSheet[]> => {
  const sheets = await axios
    .get<{ results: InspectionSheet[] }>(`${GET_INSPECTION_SHEET_API_URL(projectId, inspectionAreaId)}`, {
      responseType: 'json',
      headers: { 'X-Authorization': `Bearer ${access_token}` },
    })
    .then((response) => response.data.results)
    .catch((err) => {
      if (!showErrorModal) throw err
      processErrorHandler(err, ERROR_PROCESS.GET_INSPECTION_SHEET, showErrorModal)
      return []
    })

  return sheets
}

/**
 * Get all inspection items of an inspection sheet.
 *
 * @param access_token Auth0 access token
 * @param projectId Projekct ID
 * @param inspectionAreaId Inspection Area ID
 * @param inspectionSheetId Inspection Sheet ID
 * @param showErrorModal Function to show error modal
 * @returns
 */
export const getInspectionItems = async (
  access_token: string,
  projectId: string,
  inspectionAreaId: string,
  inspectionSheetId: string,
  showErrorModal?: (message: string) => void,
): Promise<InspectionItem[]> => {
  const doFetch = async (
    allItems: InspectionItem[],
    lastEvaluatedKey?: Record<string, string>,
  ): Promise<InspectionItem[]> => {
    const params: Record<string, string> = { limit: '1000' }
    if (lastEvaluatedKey) {
      params.last_evaluated_key = JSON.stringify(lastEvaluatedKey)
    }

    const response = await axios.get<{ results: InspectionItem[]; last_evaluated_key?: Record<string, string> }>(
      `${GET_INSPECTION_SHEET_API_URL(projectId, inspectionAreaId, inspectionSheetId)}/inspection-items`,
      {
        params,
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
      },
    )

    allItems.push(...response.data.results)

    if (response.data.last_evaluated_key) {
      return doFetch(allItems, response.data.last_evaluated_key)
    }

    return allItems
  }

  try {
    const items = await doFetch([])

    let volumeIndex = 1
    return items.map((row) => {
      const updated = { ...row }
      // generate ID for later use
      if (updated.user_defined_thresholds) {
        updated.user_defined_thresholds.map((obj) => ({ ...obj, key: Date.now() }))
      }

      // generate volume labelling
      if (updated.item_type === InspectionItemType.Volume) {
        if (!updated.part_name) updated.part_name = `体積 ${volumeIndex}`
        volumeIndex += 1
      }

      return updated
    })
  } catch (err) {
    if (!showErrorModal) throw err
    processErrorHandler(err, ERROR_PROCESS.GET_INSPECTION_SHEET, showErrorModal)
    return []
  }
}

/**
 * Update an inspection item.
 *
 * @param access_token Access token
 * @param projectId Project ID
 * @param inspectionAreaId Inspection Area ID
 * @param inspectionSheetId Inspection Sheet ID
 * @param item Inspection Item to be updated
 * @param showErrorModal
 */
export const patchInspectionItem = async (
  access_token: string,
  projectId: string,
  inspectionAreaId: string,
  inspectionSheetId: string,
  item: Partial<InspectionItem> & Pick<InspectionItem, 'inspection_item_id'>,
  showErrorModal: (message: string) => void,
  signal?: AbortSignal,
): Promise<InspectionItem | null> => {
  if (!item.inspection_item_id) {
    return null
  }

  // Remove any null values as API will reject them
  const preDefinedThresholds: InspectionItemPredefinedThresholds = {}
  if (item.pre_defined_thresholds) {
    ;(Object.keys(item.pre_defined_thresholds) as InspectionItemPreDefinedThresholdsKey[]).forEach((key) => {
      if (typeof item.pre_defined_thresholds![key] === 'number') {
        preDefinedThresholds[key] = item.pre_defined_thresholds![key]
      }
    })
  }

  // Remove id from user-defined fields
  let userDefinedThresholds: InspectionItemUserDefinedThresholds[] = []
  if (item.user_defined_thresholds) {
    userDefinedThresholds = item.user_defined_thresholds.map((row) => ({ name: row.name, value: row.value }))
  }

  return axios
    .patch<InspectionItem>(
      `${GET_INSPECTION_SHEET_API_URL(projectId, inspectionAreaId, inspectionSheetId)}/inspection-items/${
        item.inspection_item_id
      }`,
      // only send specific keys, not entire inspection item object
      {
        part_name: item.part_name,
        need_update: item.need_update,
        pre_defined_thresholds: preDefinedThresholds,
        user_defined_thresholds: userDefinedThresholds,
        is_shown_on_final_sheet: item.is_shown_on_final_sheet,
        length_with_distance_tool: item.length_with_distance_tool,
        polyline_width: item.polyline_width,
        camera_profile: item.camera_profile,
      },
      {
        signal,
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
      },
    )
    .then((res) => res.data)
    .catch((err) => {
      // istanbul ignore next can't test DOMEException throws so ignore it from coverage. Test would have tested with CanceledError.
      // if signal is provided and the request is aborted, ignore it. It would have been intentional.
      if (signal && ((err instanceof DOMException && err.name === 'AbortError') || err instanceof CanceledError)) {
        return null
      }

      processErrorHandler(err, ERROR_PROCESS.MODIFY_INSPECTION_SHEET, showErrorModal)
      return null
    })
}

/**
 * Update inspection item in batches.They must all belong to the same project.
 * They can belong to different inspection areas and inspection sheets.
 *
 * @param access_token Access token
 * @param projectId Project ID
 * @param item Inspection Item to be updated
 * @param showErrorModal
 */
export const patchInspectionItems = async (
  access_token: string,
  projectId: string,
  items: Partial<InspectionItem>[],
  showErrorModal?: (message: string) => void,
  signal?: AbortSignal,
): Promise<InspectionItem[] | null> => {
  // Remove any null values as API will reject them
  const data = items
    .map((item) => {
      if (!item.inspection_item_id) {
        return null
      }

      // Extract inspection area and inspection sheet ids, first directly from itself,
      // then if not available from partition_key.
      let ids = { inspectionAreaId: item.inspection_area_id, inspectionSheetId: item.inspection_sheet_id }
      if ((!ids.inspectionAreaId || !ids.inspectionSheetId) && item.partition_key) {
        ids = parseInspectionItemPartitionKey(item.partition_key)
        if (!ids.inspectionAreaId || !ids.inspectionSheetId) {
          return null
        }
      }

      // if we still don't have ids, skip this item
      if (!ids.inspectionAreaId || !ids.inspectionSheetId) {
        return null
      }

      let preDefinedThresholds: InspectionItemPredefinedThresholds | undefined
      if (item.pre_defined_thresholds) {
        preDefinedThresholds = {}
        ;(Object.keys(item.pre_defined_thresholds) as InspectionItemPreDefinedThresholdsKey[]).forEach((key) => {
          if (typeof item.pre_defined_thresholds![key] === 'number') {
            preDefinedThresholds![key] = item.pre_defined_thresholds![key]
          }
        })
      }

      // Remove id from user-defined fields
      let userDefinedThresholds: InspectionItemUserDefinedThresholds[] | undefined
      if (item.user_defined_thresholds) {
        userDefinedThresholds = item.user_defined_thresholds.map((row) => ({ name: row.name, value: row.value }))
      }

      // only send specific keys, not entire inspection item object
      return {
        inspection_item_id: item.inspection_item_id,
        inspection_area_id: ids.inspectionAreaId,
        inspection_sheet_id: ids.inspectionSheetId,
        params: {
          part_name: item.part_name,
          pre_defined_thresholds: preDefinedThresholds,
          user_defined_thresholds: userDefinedThresholds,
          is_shown_on_final_sheet: item.is_shown_on_final_sheet,
          length_with_distance_tool: item.length_with_distance_tool,
          polyline_length: item.polyline_length,
          polyline_width: item.polyline_width,
          camera_profile: item.camera_profile,
        },
      }
    })
    .filter(Boolean)

  if (data.length === 0) return null

  return axios
    .patch<InspectionItem[]>(`${GET_RPOJECTS_API_URL(projectId)}/inspection-items`, data, {
      signal,
      responseType: 'json',
      headers: { 'X-Authorization': `Bearer ${access_token}` },
    })
    .then((res) => res.data)
    .catch((err) => {
      // istanbul ignore next can't test DOMEException throws so ignore it from coverage. Test would have tested with CanceledError.
      // if signal is provided and the request is aborted, ignore it. It would have been intentional.
      if (signal && ((err instanceof DOMException && err.name === 'AbortError') || err instanceof CanceledError)) {
        return null
      }

      if (!showErrorModal) throw err

      processErrorHandler(err, ERROR_PROCESS.MODIFY_INSPECTION_SHEET, showErrorModal)
      return null
    })
}

/**
 * Debounced version of updateInspectionItems.
 */
export const debouncedUpdateInspectionItems = debounce(
  async (
    getAccessToken: () => Promise<string>,
    project_id: string,
    inspectionItems: Partial<InspectionItem>[],
    showErrorModal: (message: string) => void,
    beforeSaving: () => void,
    onSaved: (result: InspectionItem[]) => void,
    setIsSavingCb: (isSaving: boolean) => void,
  ) => {
    beforeSaving()
    setIsSavingCb(true)
    const token = await getAccessToken()
    if (!token) {
      return
    }

    const result = await patchInspectionItems(token, project_id, inspectionItems, showErrorModal)
    if (!result) {
      return
    }

    onSaved(result)
    setIsSavingCb(false)
  },
  4000,
)

/**
 * Non-debounced batch update for element names.
 * This is the core logic without debouncing, used by the unified batch save.
 *
 * @param getAccessToken Function to get the access token
 * @param project_id Project ID
 * @param editedItemIds List of inspection_item_ids that have been edited
 * @param inspectionItems Current inspection items from Redux (with optimistically updated names)
 * @param showErrorModal Function to show error modal
 * @param fetchInspectionItems Function to refetch inspection items on error
 */
export const updateElementNames = async (
  getAccessToken: () => Promise<string>,
  project_id: string,
  editedItemIds: string[],
  inspectionItems: InspectionItem[],
  showErrorModal: (message: string) => void,
  fetchInspectionItems: () => Promise<void>,
) => {
  if (!editedItemIds.length) return

  const token = await getAccessToken()
  if (!token) return

  // Build the list of items to update from edited inspection_item_ids
  // Names have already been optimistically updated in Redux
  const itemsToUpdate: Partial<InspectionItem>[] = []

  editedItemIds.forEach((inspectionItemId) => {
    // Find the inspection item in the store (which has the optimistic name)
    const inspectionItem = inspectionItems.find((i) => i.inspection_item_id === inspectionItemId)
    if (!inspectionItem) return

    // Add to batch
    itemsToUpdate.push({
      inspection_item_id: inspectionItemId,
      inspection_area_id: inspectionItem.inspection_area_id,
      inspection_sheet_id: inspectionItem.inspection_sheet_id,
      partition_key: inspectionItem.partition_key,
      part_name: inspectionItem.part_name,
    })
  })

  if (itemsToUpdate.length === 0) return

  // Send batch request
  const result = await patchInspectionItems(token, project_id, itemsToUpdate, showErrorModal)

  if (!result) {
    // On error, refetch to reset state
    await fetchInspectionItems()
    showErrorModal('要素名の保存中にエラーが発生しました。\n保存前の状態にデータを戻しました。')
  }
}

/**
 * Add an inspection item.
 *
 * @param access_token Auth0 access token
 * @param projectId Project ID
 * @param inspectionAreaId Inspection Area ID
 * @param inspectionSheetId Inspection Sheet ID
 * @param item Inspection Item to be added
 * @param showErrorModal Function to show error modal
 */
export const postInspectionItem = async (
  access_token: string,
  projectId: string,
  inspectionAreaId: string,
  inspectionSheetId: string,
  item: Partial<InspectionItem>,
  showErrorModal: (message: string) => void,
): Promise<InspectionItem | null> => {
  const result = await postInspectionItems(
    access_token,
    projectId,
    inspectionAreaId,
    inspectionSheetId,
    [item],
    showErrorModal,
  )
  return result ? result[0] : null
}

/**
 * Batch add inspection items.
 *
 * @param access_token Auth0 access token
 * @param projectId Project ID
 * @param inspectionAreaId Inspection Area ID
 * @param inspectionSheetId Inspection Sheet ID
 * @param items Inspection Items to be added
 * @param showErrorModal Function to show error modal
 */
export const postInspectionItems = async (
  access_token: string,
  projectId: string,
  inspectionAreaId: string,
  inspectionSheetId: string,
  items: Partial<InspectionItem>[],
  showErrorModal?: (message: string) => void,
): Promise<InspectionItem[] | null> => {
  const result = await axios
    .post<InspectionItem[]>(
      `${GET_INSPECTION_SHEET_API_URL(projectId, inspectionAreaId, inspectionSheetId)}/inspection-items:batchCreate`,
      items,
      {
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
      },
    )
    .then((res) => res.data)
    .catch((err) => {
      if (!showErrorModal) throw err
      processErrorHandler(err, ERROR_PROCESS.MODIFY_INSPECTION_SHEET, showErrorModal)
      return null
    })

  return result
}

/**
 * Delete an inspection item.
 *
 * @param access_token Auth0 access token
 * @param projectId Project ID of the inspection item
 * @param inspectionAreaId Inspection Area ID of the inspection item
 * @param inspectionSheetId Inspection Sheet ID of the inspection item
 * @param inspectionItemId Inspection Item ID to be deleted
 * @param showErrorModal Function to show error modal
 */
export const deleteInspectionItem = async (
  access_token: string,
  projectId: string,
  inspectionAreaId: string,
  inspectionSheetId: string,
  inspectionItemId: string,
  showErrorModal: (message: string) => void,
): Promise<boolean> => {
  const result = await axios
    .delete(
      `${GET_INSPECTION_SHEET_API_URL(
        projectId,
        inspectionAreaId,
        inspectionSheetId,
      )}/inspection-items/${inspectionItemId}`,
      {
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
      },
    )
    .then(() => true)
    .catch((err) => {
      processErrorHandler(err, ERROR_PROCESS.MODIFY_INSPECTION_SHEET, showErrorModal)
      return false
    })

  return result
}

/**
 * Delete an inspection item.
 *
 * @param access_token Auth0 access token
 * @param projectId Project ID of the inspection item
 * @param inspectionAreaId Inspection Area ID of the inspection item
 * @param inspectionSheetId Inspection Sheet ID of the inspection item
 * @param inspectionItemId Inspection Item ID to be deleted
 * @param showErrorModal Function to show error modal
 */
export const batchDeleteInspectionItems = async (
  access_token: string,
  projectId: string,
  inspectionAreaId: string,
  inspectionSheetId: string,
  inspectionItemIds: string[],
  showErrorModal?: (message: string) => void,
): Promise<boolean> => {
  const result = await axios
    .post(
      `${GET_INSPECTION_SHEET_API_URL(projectId, inspectionAreaId, inspectionSheetId)}/inspection-items:batchDelete`,
      { inspection_item_ids: inspectionItemIds },
      {
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
      },
    )
    .then(() => true)
    .catch((err) => {
      if (showErrorModal) {
        processErrorHandler(err, ERROR_PROCESS.MODIFY_INSPECTION_SHEET, showErrorModal)
        return false
      }

      throw err
    })

  return result
}

/**
 * Update an inspection sheet.
 *
 * @param access_token Auth0 access token
 * @param projectId Project ID
 * @param inspectionAreaId Inspection Area ID
 * @param inspectionSheet Inspection Sheet to be updated
 * @param showErrorModal Function to show error modal
 */
export const patchInspectionSheet = async (
  access_token: string,
  projectId: string,
  inspectionAreaId: string,
  inspectionSheet: InspectionSheet,
  showErrorModal: (message: string) => void,
): Promise<boolean> => {
  // partion_key is not allowed to be updated
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { inspection_sheet_id, partition_key, ...values } = inspectionSheet

  if (!inspection_sheet_id) {
    return false
  }

  const result = await axios
    .patch<{ results: InspectionSheet[] }>(
      `${GET_INSPECTION_SHEET_API_URL(projectId, inspectionAreaId, inspection_sheet_id)}`,
      { ...values },
      {
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
      },
    )
    .then(() => true)
    .catch((err) => {
      processErrorHandler(err, ERROR_PROCESS.MODIFY_INSPECTION_SHEET, showErrorModal)
      return false
    })

  return result
}

/**
 * Update inspection sheet in batches.
 *
 * @param access_token Auth0 access token
 * @param projectId Project ID
 * @param inspectionAreaId Inspection Area ID
 * @param inspectionSheets Inspection Sheets to be updated. Must contain inspection_area_id.
 * @param showErrorModal Function to show error modal
 * @returns Updated inspection sheets
 */
export const patchInspectionSheets = async (
  access_token: string,
  projectId: string,
  inspectionSheets: InspectionSheet[],
  showErrorModal?: (message: string) => void,
): Promise<InspectionSheet[] | null> => {
  // Filter out invalid inspection sheets
  const validSheets = inspectionSheets.filter((sheet) => sheet.inspection_sheet_id && sheet.inspection_area_id)
  if (!validSheets.length) {
    return null
  }

  // Prepare data
  const data = validSheets.map(
    ({
      inspection_sheet_id,
      inspection_area_id,
      creator_name,
      create_time_user_specified,
      construction_properties,
      observer_name,
      observe_time_user_specified,
    }) => ({
      inspection_sheet_id,
      inspection_area_id,
      params: {
        creator_name,
        create_time_user_specified,
        observer_name,
        observe_time_user_specified,
        construction_properties,
      },
    }),
  )

  const request = axios
    .patch<InspectionSheet[]>(`${GET_RPOJECTS_API_URL(projectId)}/inspection-sheets`, data, {
      responseType: 'json',
      headers: { 'X-Authorization': `Bearer ${access_token}` },
    })
    .then((res) => res.data)

  if (showErrorModal) {
    return request.catch((err) => {
      processErrorHandler(err, ERROR_PROCESS.MODIFY_INSPECTION_SHEET, showErrorModal)
      return null
    })
  }

  return request
}

// https://github.com/DataLabs-Japan/tekkin-app/issues/26#issuecomment-1309844610
/**
 *
 * @param values
 * @param criterion
 * @param referValue
 * @param needScaleValueUp need to convert meter to millimeter
 * @param emptyIsValid if both estimated and specified values are null, return valid
 * @returns
 *   - null: values are not valid (not exists)
 *   - true: passed
 *   - false: not passed
 */
export const evaluateInspectionItemValue = (
  values: InspectionItemNumberValues | undefined,
  criterion: number,
  referValue?: number | null,
  needScaleValueUp?: boolean,
  emptyIsValid?: boolean,
): { result: boolean | null; description: string } => {
  if (values && emptyIsValid && values.estimated_value === null && values.specified_value === null) {
    return {
      result: true,
      description: '',
    }
  }

  if (values === undefined || values.estimated_value === null || values.specified_value === null) {
    return {
      result: null,
      description: '',
    }
  }

  const result = {
    result: null,
    description: '',
  }

  // values are not valid (not exists)
  if (values.estimated_value === undefined || values.specified_value === undefined) {
    return result
  }

  // Computes criterion_contractee/criterion_contractor using designed value of diameter:
  const criterionReference = referValue !== undefined && referValue !== null ? referValue * criterion : criterion
  // Computes difference of designed/estimated values.
  // The order is estimated value - designed value:
  const difference = values.estimated_value - values.specified_value

  // For description
  const scaledUpDifference = needScaleValueUp ? meterToMillimeter(difference) : difference
  const differenceDescription = `${roundNumber(scaledUpDifference, EDITOR_DECIMAL_BASE)}`

  const scaledUpReferValue = !referValue ? referValue : meterToMillimeter(referValue)
  const scaledUpCriterionReference = needScaleValueUp ? meterToMillimeter(criterionReference) : criterionReference
  const referenceDescription =
    scaledUpReferValue !== undefined && scaledUpReferValue !== null && criterion !== 0
      ? `${roundNumber(scaledUpReferValue, EDITOR_DECIMAL_BASE)} * ${roundNumber(
          criterion,
          EDITOR_DECIMAL_BASE,
        )} = ${roundNumber(scaledUpCriterionReference, EDITOR_DECIMAL_BASE)}`
      : `${criterion}`

  // Check whether the difference is within the criterion
  if (Math.abs(difference) <= criterionReference) {
    return {
      result: true,
      description: `(${differenceDescription}) <= ±(${referenceDescription})`,
    }
  }
  return {
    result: false,
    description: `(${differenceDescription}) > ±(${referenceDescription})`,
  }
}

export const scaleUpValues = (
  values: InspectionItemNumberValues | undefined,
  fallbackSpecifiedValue: number | undefined,
  fallbackEstimatedValue: number | undefined,
) => ({
  specified_value:
    values?.specified_value !== undefined && values.specified_value !== null
      ? meterToMillimeter(values.specified_value)
      : fallbackSpecifiedValue,
  estimated_value:
    values?.estimated_value !== undefined && values.estimated_value !== null
      ? meterToMillimeter(values.estimated_value)
      : fallbackEstimatedValue,
})
export const scaleDownValues = (values: InspectionItemNumberValues) => ({
  specified_value:
    values.specified_value !== undefined && values.specified_value !== null
      ? millimeterToMeter(values.specified_value)
      : undefined,
  estimated_value:
    values.estimated_value !== undefined && values.estimated_value !== null
      ? millimeterToMeter(values.estimated_value)
      : undefined,
})

export const evaluateValues = (
  values: InspectionItemNumberValues | null | undefined,
  contracteeCriterion: number,
  contractorCriterion: number,
  contracteeReferValue?: number | null,
  contractorReferValue?: number | null,
  needScaleValueUp?: boolean,
  emptyIsValid?: boolean,
): { color: string; description: string } => {
  if (values === null || values === undefined) {
    return { color: INSPECTION_NORMAL_COLOR, description: '' }
  }

  const contracteeResult = evaluateInspectionItemValue(
    values,
    contracteeCriterion,
    contracteeReferValue,
    needScaleValueUp,
    emptyIsValid,
  )
  if (!contracteeResult.result) {
    return { color: INSPECTION_CONTRACTEE_ERROR_COLOR, description: contracteeResult.description }
  }

  const contractorResult = evaluateInspectionItemValue(
    values,
    contractorCriterion,
    contractorReferValue,
    needScaleValueUp,
    emptyIsValid,
  )
  if (!contractorResult.result) {
    return { color: INSPECTION_CONTRACTOR_ERROR_COLOR, description: contractorResult.description }
  }

  return { color: INSPECTION_NORMAL_COLOR, description: contracteeResult.description || contractorResult.description }
}

/**
 * Checks if any of the plane item is shown on the final sheet.
 */
export const isAnyPlaneItemShownOnFinalSheet = (plane: PlaneInspectionItems): boolean =>
  plane.area?.is_shown_on_final_sheet ||
  plane.perimeter?.is_shown_on_final_sheet ||
  plane.length1?.is_shown_on_final_sheet ||
  plane.length2?.is_shown_on_final_sheet ||
  false

/**
 * Find an inspection sheet by inspection area ID from a list of inspection sheets.
 * The inspection sheet list must contain partition_key.
 *
 * @param sheets Inspection sheets
 * @param inspectionAreaId Inspection area ID
 * @returns Inspection sheet or null if not found
 */
export const findInspectionAreaSheet = (sheets: InspectionSheet[], inspectionAreaId: string): InspectionSheet | null =>
  sheets.find((sheet) => {
    if (!sheet.partition_key) return null

    const ids = parseInspectionItemPartitionKey(sheet.partition_key)
    return ids.inspectionAreaId === inspectionAreaId
  }) || null

/**
 * Get headers for diagram section of the inspection sheet.
 *
 * @param settings Inspection sheet settings
 */
export const getDiagramHeaders = (
  settings: ProjectSheetSettings,
  hasGrid = true,
): { planeHeader: string | null; gridHeader: string | null } => {
  // Combined header for Plane + Polyline can change depending on settings
  const planeHeader = (): string | null => {
    if (
      !settings.sheet_diagram_visibility ||
      (settings.sheet_diagram_visibility.plane_diagram && settings.sheet_diagram_visibility.polyline_diagram)
    ) {
      return '平面図・延長図'
    }

    if (
      hasGrid &&
      settings.sheet_diagram_visibility.grid_diagram &&
      settings.sheet_diagram_visibility.polyline_diagram
    ) {
      return 'グリッド図面・延長図'
    }

    if (settings.sheet_diagram_visibility.polyline_diagram) {
      return '延長図'
    }

    if (settings.sheet_diagram_visibility.plane_diagram) {
      return '平面図'
    }

    return null
  }

  // Combined header for Grid + Polyline can change depending on settings
  const gridHeader = (): string | null => {
    // if there's only grid left
    if (
      !settings.sheet_diagram_visibility ||
      (settings.sheet_diagram_visibility.grid_diagram && !settings.sheet_diagram_visibility.polyline_diagram)
    ) {
      return 'グリッド図面'
    }

    // if plane is hidden, grid gets 'moved' to plane header
    // and of course if grid is hidden, header should be hidden too
    if (!settings.sheet_diagram_visibility.plane_diagram || !settings.sheet_diagram_visibility.grid_diagram) {
      return null
    }

    return 'グリッド図面'
  }

  return { planeHeader: planeHeader(), gridHeader: gridHeader() }
}

/**
 * Checks if the model is visible on the inspection sheet.
 *
 * @param settings Project sheet settings
 * @param itemType Type of the inspection item
 */
export const checkIsModelVisible = (settings: ProjectSheetSettings, itemType?: InspectionItemType): boolean => {
  if (!settings.sheet_models_visibility || !itemType) {
    return false
  }

  switch (itemType) {
    case InspectionItemType.Volume:
      return settings.sheet_models_visibility.volume
    case InspectionItemType.PolygonArea:
      return settings.sheet_models_visibility.plane
    case InspectionItemType.PolylineLength:
      return settings.sheet_models_visibility.polyline
    case InspectionItemType.LengthWithDistanceTool:
      return settings.sheet_models_visibility.length_with_distance_tool
    default:
      return false
  }
}

/**
 * Generates a default part name for an inspection item.
 * If the item does not have a part name, it will use the label from the labelMap
 *
 * @param item Inspection item to generate default part name for
 * @param index Index of the item in the list (used for numbering)
 * @returns
 */
export const makeDefaultPartName = (item: InspectionItem, index: number): InspectionItem => {
  switch (item.item_type) {
    case InspectionItemType.Volume:
      return { ...item, part_name: item.part_name || `体積 ${index + 1}` }
    case InspectionItemType.PolygonArea:
      return { ...item, part_name: item.part_name || `面積${zeroPad(index + 1, 3)}` }
    case InspectionItemType.PolylineLength:
      return { ...item, part_name: item.part_name || `延長 ${zeroPad(index + 1, 3)}` }
    case InspectionItemType.LengthWithDistanceTool:
      return { ...item, part_name: item.part_name || `距離${zeroPad(index + 1, 3)}` }
    default:
      return item
  }
}
